import React, { useEffect } from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import { observer } from 'mobx-react-lite';
import agent from '../../../../app/api/agent';
import { useStore } from '../../../../app/stores/store';
import { getImageSrc } from '../../../../utils/ImageHelper';

const styles = StyleSheet.create({
  page: { padding: 8, fontSize: 9, fontFamily: 'Helvetica' },

  header: {
    backgroundColor: '#f2cb05',
    textAlign: 'center',
    padding: 4,
    fontWeight: 'bold',
    fontSize: 10,
    border: '1pt solid black',
  },
  subheader: {
    backgroundColor: '#d9d6d6ff',
    textAlign: 'center',
    padding: 3,
    fontWeight: 'bold',
    fontSize: 9,
    borderLeft: '1pt solid black',
    borderRight: '1pt solid black',
    borderBottom: '1pt solid black',
  },

  row: {
    flexDirection: 'row',
    borderLeft: '1pt solid black',
    borderRight: '1pt solid black',
    borderBottom: '1pt solid black',
  },
  cell: {
    flex: 1,
    padding: 4,
    borderRight: '1pt solid black',
    fontSize: '8px',
  },
  cellHeader: {
    flex: 1,
    padding: 4,
    fontWeight: 'bold',
    backgroundColor: '#D0CECE',
    borderRight: '1pt solid black',
    fontSize: '8px',
  },

  section: {
    marginBottom: 0,
  },

  noRightBorder: {
    borderRight: 'none',
  },
  checkbox: {
    width: 10,
    height: 10,
    marginRight: 4,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  borderedBox: {
    borderWidth: 1,
    borderColor: 'black',
    padding: 5,
    marginBottom: 10,
    borderStyle: 'solid',
    borderTop: 'none',
  },
});

interface FFV1ReportsPDFProps {
  localElement: any[];
  metadata: any[];
  name: string;
  subtaskId: string;
  isDualPDF: boolean;
}

const FFV1ReportsPDF: React.FC<FFV1ReportsPDFProps> = ({
  name,
  metadata,
  localElement,
  subtaskId,
  isDualPDF,
}) => {
  // Remove or fix this line. If you need the store, use:
  const { masteringStore, activityStore } = useStore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await agent.NFAI.Ffv1QcReport(
          activityStore.selectedActivity?.id,
          subtaskId,
        ); // your API call
        masteringStore.FFV1ReportInfo = response.data;
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
    //eslint-disable-next-line
  }, [metadata]);

  const dyn = (() => {
    try {
      const rawValue = masteringStore.FFV1ReportInfo?.[29]?.elementValue;
      if (!rawValue) return 0;
      return JSON.parse(rawValue.replace('DynamicTable|', ''))?.length || 0;
    } catch {
      return 0;
    }
  })();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Text style={styles.header}>
          {name === 'DPX QC Report'
            ? 'Color graded (P3) and restored - DPX (FFV1 rawcooked) - QUALITY CHECK REPORT'
            : 'Digital Source Master (FFV1 rawcooked) -  QUALITY CHECK REPORT'}
        </Text>
        <Text style={styles.subheader}>NFHM Restoration Project</Text>
        <Text style={styles.subheader}>
          PICTURE VENDOR NAME: Prasad Corporation Pvt Ltd, Chennai
        </Text>

        {/* Section 1 */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.cellHeader}>
              {masteringStore.FFV1ReportInfo[0]?.elementName}
            </Text>
            <Text style={styles.cell}>
              {masteringStore.FFV1ReportInfo[0]?.elementValue}
            </Text>
            <Text style={styles.cell}>
              {masteringStore.FFV1ReportInfo[9]?.elementName}:
            </Text>
            <Text style={[styles.cell, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[9]?.elementValue}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.cellHeader}>
              {masteringStore.FFV1ReportInfo[1]?.elementName}
            </Text>
            <Text style={styles.cell}>
              {masteringStore.FFV1ReportInfo[1]?.elementValue}
            </Text>
            <Text style={styles.cell}>
              {masteringStore.FFV1ReportInfo[10]?.elementName}:
            </Text>
            <Text style={[styles.cell, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[10]?.elementValue}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.cellHeader}>
              {masteringStore.FFV1ReportInfo[2]?.elementName}
            </Text>
            <Text style={styles.cell}>
              {masteringStore.FFV1ReportInfo[2]?.elementValue}
            </Text>
            <Text style={styles.cell}>
              {masteringStore.FFV1ReportInfo[11]?.elementName}:
            </Text>
            <View
              style={[styles.cell, styles.noRightBorder, styles.checkboxRow]}
            >
              <Image
                src={
                  masteringStore.FFV1ReportInfo[11]?.elementValue === '4K'
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>4K</Text>

              <Image
                src={
                  masteringStore.FFV1ReportInfo[11]?.elementValue === '2K'
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />
              <Text>2K</Text>
            </View>
          </View>

          <View style={styles.row}>
            <Text
              style={[styles.cellHeader, { borderBottom: '0.1px solid black' }]}
            >
              {masteringStore.FFV1ReportInfo[3]?.elementName}
            </Text>
            <Text style={styles.cell}>
              {masteringStore.FFV1ReportInfo[3]?.elementValue}
            </Text>
            {name === 'DPX QC Report' ? (
              <Text
                style={[styles.cell, styles.noRightBorder, { flex: 2.07 }]}
              ></Text>
            ) : (
              <>
                <Text style={[styles.cell, { flex: 1 }]}>
                  {masteringStore.FFV1ReportInfo[12]?.elementName}:{' '}
                </Text>
                <Text style={[styles.cell, styles.noRightBorder, { flex: 1 }]}>
                  {masteringStore.FFV1ReportInfo[12]?.elementValue}
                </Text>
              </>
            )}
          </View>
         {name === 'DPX QC Report' ? 
         (
          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[4]?.elementName}
            </Text>
            <View
              style={[
                styles.cell,
                { flex: 6 },
                styles.noRightBorder,
                styles.checkboxRow,
              ]}
            >
              <Image
                src={
                  (masteringStore.FFV1ReportInfo[4]?.elementValue ?? '')
                    .toUpperCase()
                    .replace(/[^A-Z]/g, '')
                    .includes('BW')
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>B&W</Text>

              <Image
                src={
                  ['COLOR', 'COLOUR'].some((keyword) =>
                    (masteringStore.FFV1ReportInfo[4]?.elementValue ?? '')
                      .toUpperCase()
                      .replace(/[^A-Z]/g, '')
                      .includes(keyword),
                  )
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />

              <Text style={{ marginRight: '40px' }}>COLOR</Text>

              <Image
                src={
                  (masteringStore.FFV1ReportInfo[4]?.elementValue ?? '')
                    .toUpperCase()
                    .includes('LOG')
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>LOG</Text>
              <Image
                src={
                  (masteringStore.FFV1ReportInfo[4]?.elementValue ?? '')
                    .toUpperCase()
                    .includes('LINEAR')
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />
              <Text>LINEAR</Text>
            </View>
          </View>
          ) :  
          (
          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[4]?.elementName}
            </Text>
            <View
              style={[
                styles.cell,
                { flex: 6 },
                styles.noRightBorder,
                styles.checkboxRow,
              ]}
            >
              <Image
                src={
                  (masteringStore.FFV1ReportInfo[4]?.elementValue ?? '')
                    .toUpperCase()
                    .replace(/[^A-Z]/g, '')
                    .includes('BW')
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>B&W</Text>

              <Image
                src={
                  ['COLOR', 'COLOUR'].some((keyword) =>
                    (masteringStore.FFV1ReportInfo[4]?.elementValue ?? '')
                      .toUpperCase()
                      .replace(/[^A-Z]/g, '')
                      .includes(keyword),
                  )
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />

              <Text style={{ marginRight: '40px' }}>COLOR</Text>

              <Image
                src={
                   '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>LOG</Text>
              <Image
                src={
                    '/assets/NFAI_Restoration_Checkbox_Checked.png'
 
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />
              <Text>LINEAR</Text>
            </View>
          </View>
          )}

          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[5]?.elementName}
            </Text>

            <View
              style={[
                styles.cell,
                { flex: 6 },
                styles.noRightBorder,
                styles.checkboxRow,
              ]}
            >
              <Image
                src={
                  masteringStore.FFV1ReportInfo[5]?.elementValue?.includes('10')
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>10 bit</Text>

              <Image
                src={
                  masteringStore.FFV1ReportInfo[5]?.elementValue?.includes('16')
                    ? '/assets/NFAI_Restoration_Checkbox_Checked.png'
                    : '/assets/NFAI_Restoration_Checkbox_Unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />
              <Text>16 bit</Text>
            </View>
          </View>

          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[6]?.elementName}
            </Text>
            <Text style={[styles.cell, { flex: 6 }, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[6]?.elementValue}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[7]?.elementName}
            </Text>
            <Text style={[styles.cell, { flex: 6 }, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[7]?.elementValue}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[8]?.elementName}
            </Text>
            <Text style={[styles.cell, { flex: 6 }, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[8]?.elementValue}
            </Text>
          </View>
        </View>

        {/* Section 2 */}
        <View style={styles.section}>
          {name !== 'DPX QC Report' && (
            <>
              <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
                <Text style={[styles.cell, styles.noRightBorder]}>
                  {masteringStore.FFV1ReportInfo[24]?.elementName}:
                </Text>
                <Text style={[styles.cell, { flex: 6 }, styles.noRightBorder]}>
                  {masteringStore.FFV1ReportInfo[24]?.elementValue}
                </Text>
              </View>
              <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
                <Text style={[styles.cell, styles.noRightBorder, { flex: 2 }]}>
                  {masteringStore.FFV1ReportInfo[23]?.elementName}:
                </Text>
                <Text style={[styles.cell, { flex: 5 }, styles.noRightBorder]}>
                  {masteringStore.FFV1ReportInfo[23]?.elementValue}
                </Text>
              </View>
              <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
                <Text style={[styles.cell, styles.noRightBorder]}>
                  {masteringStore.FFV1ReportInfo[25]?.elementName}:
                </Text>

                <View
                  style={[
                    styles.cell,
                    styles.noRightBorder,
                    styles.checkboxRow,
                  ]}
                >
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[25]?.elementValue?.toUpperCase() ===
                      'YES'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={styles.checkbox}
                  />
                  <Text>YES</Text>

                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[25]?.elementValue?.toUpperCase() ===
                      'NO'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={[styles.checkbox, { marginLeft: 10 }]}
                  />
                  <Text>NO</Text>
                </View>
              </View>
            </>
          )}
          <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
            <Text style={[styles.cell, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[13]?.elementName}
            </Text>

            <View
              style={[styles.cell, styles.noRightBorder, styles.checkboxRow]}
            >
              <Image
                src={
                  masteringStore.FFV1ReportInfo[13]?.elementValue?.toUpperCase() ===
                  'YES'
                    ? '/assets/checked.jpg'
                    : '/assets/unchecked.png'
                }
                style={styles.checkbox}
              />

              <Text>YES</Text>

              <Image
                src={
                  masteringStore.FFV1ReportInfo[13]?.elementValue?.toUpperCase() ===
                  'NO'
                    ? '/assets/checked.jpg'
                    : '/assets/unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />
              <Text>NO</Text>
            </View>
          </View>

          <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
            <Text style={[styles.cell, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[14]?.elementName}:
            </Text>

            <View
              style={[styles.cell, styles.noRightBorder, styles.checkboxRow]}
            >
              <Image
                src={
                  masteringStore.FFV1ReportInfo[14]?.elementValue?.toUpperCase() ===
                  'YES'
                    ? '/assets/checked.jpg'
                    : '/assets/unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>YES</Text>

              <Image
                src={
                  masteringStore.FFV1ReportInfo[14]?.elementValue?.toUpperCase() ===
                  'NO'
                    ? '/assets/checked.jpg'
                    : '/assets/unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />
              <Text>NO</Text>
            </View>
          </View>

          {name === 'DPX QC Report' ? (
            <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
              <Text style={[styles.cell, styles.noRightBorder]}>
                {masteringStore.FFV1ReportInfo[15]?.elementName}:
              </Text>

              <View
                style={[styles.cell, styles.noRightBorder, styles.checkboxRow]}
              >
                <Image
                  src={
                    masteringStore.FFV1ReportInfo[15]?.elementValue?.toUpperCase() ===
                    'YES'
                      ? '/assets/checked.jpg'
                      : '/assets/unchecked.png'
                  }
                  style={styles.checkbox}
                />
                <Text>YES</Text>

                <Image
                  src={
                    masteringStore.FFV1ReportInfo[15]?.elementValue?.toUpperCase() ===
                    'NO'
                      ? '/assets/checked.jpg'
                      : '/assets/unchecked.png'
                  }
                  style={[styles.checkbox, { marginLeft: 10 }]}
                />
                <Text>NO</Text>
              </View>
            </View>
          ) : (
            <>
              {/* MKV STREAM FORMATION */}
              <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
                <Text style={[styles.cellHeader, { flex: 1.9 }]}>
                  {masteringStore.FFV1ReportInfo[28]?.elementName}
                </Text>
                <View
                  style={[
                    styles.cell,
                    { flex: 6 },
                    styles.noRightBorder,
                    styles.checkboxRow,
                  ]}
                >
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[28]?.elementValue?.toUpperCase() ===
                      'SEAMLESS'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={styles.checkbox}
                  />
                  <Text>SEAMLESS</Text>
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[28]?.elementValue?.toUpperCase() ===
                      'REEL-WISE'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={[styles.checkbox, { marginLeft: 10 }]}
                  />
                  <Text>REEL-WISE</Text>
                </View>
              </View>

              {/* AUDIO PRESENT IN MKV */}
              <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
                <Text style={[styles.cellHeader, { flex: 1.9 }]}>
                  {masteringStore.FFV1ReportInfo[26]?.elementName}
                </Text>
                <View
                  style={[
                    styles.cell,
                    { flex: 6 },
                    styles.noRightBorder,
                    styles.checkboxRow,
                  ]}
                >
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[26]?.elementValue?.toUpperCase() ===
                      'YES'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={styles.checkbox}
                  />
                  <Text>YES</Text>
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[26]?.elementValue?.toUpperCase() ===
                      'NO'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={[styles.checkbox, { marginLeft: 10 }]}
                  />
                  <Text>NO</Text>
                </View>
              </View>

              {/* RESTORED AUDIO FORMAT - Cinemix 48kHz */}
              <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
                <Text style={[styles.cell, styles.noRightBorder]}>
                  {masteringStore.FFV1ReportInfo[27]?.elementName}:
                </Text>
                <View
                  style={[
                    styles.cell,
                    styles.noRightBorder,
                    styles.checkboxRow,
                  ]}
                >
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[27]?.elementValue?.toUpperCase() ===
                      'YES'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={styles.checkbox}
                  />
                  <Text>YES</Text>
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[27]?.elementValue?.toUpperCase() ===
                      'NO'
                        ? '/assets/checked.jpg'
                        : '/assets/unchecked.png'
                    }
                    style={[styles.checkbox, { marginLeft: 10 }]}
                  />
                  <Text>NO</Text>
                </View>
              </View>
            </>
          )}

          <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[16]?.elementName}:
            </Text>

            <View
              style={[
                styles.cell,
                { flex: 6 },
                styles.noRightBorder,
                styles.checkboxRow,
              ]}
            >
              <Image
                src={
                  masteringStore.FFV1ReportInfo[16]?.elementValue?.toUpperCase() ===
                  'YES'
                    ? '/assets/checked.jpg'
                    : '/assets/unchecked.png'
                }
                style={styles.checkbox}
              />
              <Text>YES</Text>

              <Image
                src={
                  masteringStore.FFV1ReportInfo[16]?.elementValue?.toUpperCase() ===
                  'NO'
                    ? '/assets/checked.jpg'
                    : '/assets/unchecked.png'
                }
                style={[styles.checkbox, { marginLeft: 10 }]}
              />
              <Text>NO</Text>
            </View>
          </View>

          <View style={[styles.row, { backgroundColor: '#D0CECE' }]}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>
              {masteringStore.FFV1ReportInfo[17]?.elementName}:
            </Text>

            <Text style={[styles.cell, { flex: 6 }, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[17]?.elementValue}
            </Text>
          </View>
        </View>

        {/* Section 3 */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1.9 }]}>REEL NOS.</Text>
            <Text
              style={[styles.cellHeader, { flex: 6 }, styles.noRightBorder]}
            >
              CHECKSUM VALUE OF MKV
            </Text>
          </View>

          {(() => {
            const dynamicTableIndex = 29;
            const rawValue =
              masteringStore.FFV1ReportInfo?.[dynamicTableIndex]?.elementValue;

            if (!rawValue || !rawValue.startsWith('DynamicTable|')) return null;

            try {
              const tableData = JSON.parse(
                rawValue.replace('DynamicTable|', ''),
              );

              return tableData.map((row: any, i: number) => (
                <View style={styles.row} key={row._uuid_ || i}>
                  <Text style={[styles.cell, { flex: 1.9 }]}>
                    {row['REEL NO'] || ''}
                  </Text>
                  <Text
                    style={[styles.cell, { flex: 6 }, styles.noRightBorder]}
                  >
                    {row['CHECKSUM VALUE OF MKV'] || ''}
                  </Text>
                </View>
              ));
            } catch (err) {
              console.error('Failed to parse DynamicTable elementValue', err);
              return null;
            }
          })()}
        </View>

        {/* Section 4 */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1 }]}>
              {masteringStore.FFV1ReportInfo[18]?.elementName}:
            </Text>
          </View>
          <View style={[styles.row, { height: 40 }]}>
            <Text style={[styles.cell, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[18]?.elementValue}
            </Text>
          </View>

          {dyn >= 15 ? null : (
            <View style={styles.row}>
              <Text style={[styles.cellHeader, { flex: 1 }]}>
                {masteringStore.FFV1ReportInfo[19]?.elementName}:
              </Text>
              <Text style={[styles.cell, styles.noRightBorder]}>
                {masteringStore.FFV1ReportInfo[19]?.elementValue}
              </Text>
            </View>
          )}

          {dyn >= 15 ? null : (
            <View style={[styles.row, { height: 60 }]}>
              <Text style={[styles.cellHeader, { flex: 1 }]}>
                {masteringStore.FFV1ReportInfo[20]?.elementName}
              </Text>

              {/* Wrap Image and Date in a vertical View */}
              <View
                style={[
                  styles.cell,
                  styles.noRightBorder,
                  { alignItems: 'flex-start', justifyContent: 'center' },
                ]}
              >
                <Image
                  src={
                    masteringStore.FFV1ReportInfo[19]?.elementValue
                      ?.toLowerCase()
                      ?.includes('riaz khan')
                      ? getImageSrc('riaz khan')
                      : masteringStore.FFV1ReportInfo[19]?.elementValue
                            ?.toLowerCase()
                            ?.includes('siddharth')
                        ? getImageSrc('siddharth')
                        : masteringStore.FFV1ReportInfo[19]?.elementValue
                              ?.toLowerCase()
                              ?.includes('kalai')
                          ? getImageSrc('kalai')
                          : masteringStore.FFV1ReportInfo[19]?.elementValue
                                ?.toLowerCase()
                                ?.replaceAll(' ','')
                                ?.includes('arulkumar')
                            ? getImageSrc('arulkumar')
                            : masteringStore.FFV1ReportInfo[19]?.elementValue
                                  ?.toLowerCase()
                                  ?.includes('karthick')
                              ? getImageSrc('karthick')
                              : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8z8BQDwAF/gJ+W6Cn5QAAAABJRU5ErkJggg=='
                  }
                  style={{ width: 120, height: 30 }}
                />
                <Text style={{ marginLeft: '20px' }}>
                  {masteringStore.FFV1ReportInfo[20]?.elementValue}
                </Text>
              </View>
            </View>
          )}

          {dyn > 10 || (dyn > 10 && isDualPDF) ? null : (
            <>
              <View style={styles.row}>
                <Text style={[styles.cellHeader, { flex: 1 }]}>
                  {masteringStore.FFV1ReportInfo[21]?.elementName + ':'}
                </Text>
                <Text style={[styles.cell, styles.noRightBorder]}>
                  {masteringStore.FFV1ReportInfo[21]?.elementValue}
                </Text>
              </View>
              <View style={[styles.row, { height: 70 }]}>
                <Text style={[styles.cellHeader, { flex: 1 }]}>
                  {masteringStore.FFV1ReportInfo[22]?.elementName}
                </Text>
                <View
                  style={[
                    styles.cell,
                    styles.noRightBorder,
                    { alignItems: 'flex-start', justifyContent: 'center' },
                  ]}
                >
                  <Image
                    src={
                      masteringStore.FFV1ReportInfo[21]?.elementValue
                        ?.toLowerCase()
                        ?.includes('pramod')
                        ? getImageSrc('pramod')
                        : masteringStore.FFV1ReportInfo[21]?.elementValue
                              ?.toLowerCase()
                              ?.includes('rajan')
                          ? getImageSrc('rajan')
                          : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8z8BQDwAF/gJ+W6Cn5QAAAABJRU5ErkJggg=='
                    }
                    style={{ width: 120, height: 70 }}
                  />
                  <Text style={{ marginLeft: '20px' }}>
                    {masteringStore.FFV1ReportInfo[22]?.elementValue}
                  </Text>
                </View>
              </View>
            </>
          )}
        </View>
        {dyn > 10 || (dyn > 10 && isDualPDF)
          ? null
          : name !== 'DPX QC Report' && (
              <View style={[styles.section, styles.borderedBox]}>
                <Text style={{ fontWeight: 'bold', fontSize: 9 }}>
                  ****Sound films should have Aspect Ratio 1.37 and not 1.33
                </Text>
              </View>
            )}
        {isDualPDF ? (
          <View
            fixed
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
            }}
          >
            <Image
              src={getImageSrc('bala')}
              style={{ width: 60, height: 50, marginLeft: 10 }}
            />
          </View>
        ) : null}
      </Page>

      {dyn <= 9 ? null : <Page
        size="A4"
        style={{
          padding: 10,
          fontSize: 9,
          fontFamily: 'Helvetica',
          marginTop: 10,
        }}
      >
        {dyn >= 15 ? (
          <View style={styles.row}>
            <Text style={[styles.cellHeader, { flex: 1 }]}>
              {masteringStore.FFV1ReportInfo[19]?.elementName}:
            </Text>
            <Text style={[styles.cell, styles.noRightBorder]}>
              {masteringStore.FFV1ReportInfo[19]?.elementValue}
            </Text>
          </View>
        ) : null}
        {dyn >= 15 || (dyn > 10 && isDualPDF) ? (
          <View style={[styles.row, { height: 60 }]}>
            <Text style={[styles.cellHeader, { flex: 1 }]}>
              {masteringStore.FFV1ReportInfo[20]?.elementName}
            </Text>

            {/* Wrap Image and Date in a vertical View */}
            <View
              style={[
                styles.cell,
                styles.noRightBorder,
                { alignItems: 'flex-start', justifyContent: 'center' },
              ]}
            >
              <Image
                src={
                  masteringStore.FFV1ReportInfo[19]?.elementValue
                    ?.toLowerCase()
                    ?.includes('riaz khan')
                    ? getImageSrc('riaz khan')
                    : masteringStore.FFV1ReportInfo[19]?.elementValue
                          ?.toLowerCase()
                          ?.includes('siddharth')
                      ? getImageSrc('siddharth')
                      : masteringStore.FFV1ReportInfo[19]?.elementValue
                            ?.toLowerCase()
                            ?.includes('kalai')
                        ? getImageSrc('kalai')
                        : masteringStore.FFV1ReportInfo[19]?.elementValue
                              ?.toLowerCase()
                              ?.replaceAll(' ','')
                              ?.includes('arulkumar')
                          ? getImageSrc('arulkumar')
                          : masteringStore.FFV1ReportInfo[19]?.elementValue
                                ?.toLowerCase()
                                ?.includes('karthick')
                            ? getImageSrc('karthick')
                            : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8z8BQDwAF/gJ+W6Cn5QAAAABJRU5ErkJggg=='
                }
                style={{ width: 120, height: 30 }}
              />
              <Text style={{ marginLeft: '20px' }}>
                {masteringStore.FFV1ReportInfo[20]?.elementValue}
              </Text>
            </View>
          </View>
        ) : null}
        {dyn > 10 || (dyn > 10 && isDualPDF) ? (
          <>
            <View style={styles.row}>
              <Text style={[styles.cellHeader, { flex: 1 }]}>
                {masteringStore.FFV1ReportInfo[21]?.elementName + ':'}
              </Text>
              <Text style={[styles.cell, styles.noRightBorder]}>
                {masteringStore.FFV1ReportInfo[21]?.elementValue}
              </Text>
            </View>
            <View style={[styles.row, { height: 70 }]}>
              <Text style={[styles.cellHeader, { flex: 1 }]}>
                {masteringStore.FFV1ReportInfo[22]?.elementName}
              </Text>
              <View
                style={[
                  styles.cell,
                  styles.noRightBorder,
                  { alignItems: 'flex-start', justifyContent: 'center' },
                ]}
              >
                <Image
                  src={
                    masteringStore.FFV1ReportInfo[21]?.elementValue
                      ?.toLowerCase()
                      ?.includes('pramod')
                      ? getImageSrc('pramod')
                      : masteringStore.FFV1ReportInfo[21]?.elementValue
                            ?.toLowerCase()
                            ?.includes('rajan')
                        ? getImageSrc('rajan')
                        : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8z8BQDwAF/gJ+W6Cn5QAAAABJRU5ErkJggg=='
                  }
                  style={{ width: 120, height: 70 }}
                />
                <Text style={{ marginLeft: '20px' }}>
                  {masteringStore.FFV1ReportInfo[22]?.elementValue}
                </Text>
              </View>
            </View>
            {name !== 'DPX QC Report' && (
              <View style={[styles.section, styles.borderedBox]}>
                <Text style={{ fontWeight: 'bold', fontSize: 9 }}>
                  ****Sound films should have Aspect Ratio 1.37 and not 1.33
                </Text>
              </View>
            )}
            {isDualPDF ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-end',
                  marginTop: '2px',
                }}
              >
                <Image
                  src={getImageSrc('bala')}
                  style={{ width: 60, height: 50, marginLeft: 10 }}
                />
              </View>
            ) : null}
          </>
        ) : null}
      </Page>}
    </Document>
  );
};

export default observer(FFV1ReportsPDF);